import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';


const initialState: UserState = {
    auth: undefined,
    user: undefined,
    viewing: undefined,
    tourViewed: !!localStorage.getItem(`${process.env.VUE_APP_NAME}_TOUR_VIEWING_STATUS`) || false,
    dashboardData: undefined,
    currentLevel: undefined,
    nextPlan: undefined,
    nextTestOrQuestion: undefined,
    firstFillableStep: undefined,
    upgradeStatus: 'not_upgradeable',
    schedule: undefined,
    deviceType: '',
    dailyCigarettes: 0,
};

const rootState: UserState = initialState;

const getters: GetterTree<UserState, RootState> = {
    isLoggedIn(state: UserState): boolean {
        return !!(state.auth && state.auth.id);
    },
    auth(state: UserState): Auth | undefined {
        return state.auth;
    },
    current(state: UserState): User | undefined {
        return state.user;
    },
    viewing(state: UserState): User | undefined {
        return state.viewing;
    },
    dashboardData(state: UserState): DashboardData | undefined {
        return state.dashboardData;
    },
    schedule(state: UserState): any {
        return state.schedule;
    },
    dailyCigarettes(state: UserState): any {
        return state.dailyCigarettes;
    },
    tourViewed(state: UserState): boolean {
        return state.tourViewed;
    },
    currentLevel(state: UserState): DashboardLevel | undefined {
        return state.currentLevel;
    },
    nextTestOrQuestion(state: UserState): any {
        return state.nextTestOrQuestion;
    },
    nextPlan(state: UserState): DashboardPlan | undefined {
        return state.nextPlan;
    },
    upgradeStatus(state: UserState): string {
        return state.upgradeStatus;
    },
    firstFillableStep(state: UserState): any {
        return state.firstFillableStep;
    },
    deviceType(state: UserState): any {
        return state.deviceType;
    },
    hasNextAction(state: UserState): boolean {
        if (state.nextPlan && !state.firstFillableStep) {
            return false;
        }

        return true;
    },
};

const mutations: MutationTree<UserState> = {
    RESET(state: UserState) {
        state.auth = undefined;
        state.user = undefined;
        state.viewing = undefined;
        state.dashboardData = undefined;
        state.schedule = undefined;
        state.currentLevel = undefined;
        state.nextTestOrQuestion = undefined;
        state.nextPlan = undefined;
        state.upgradeStatus = 'not_upgradeable';
        state.firstFillableStep = undefined;
        state.dailyCigarettes = 0;
        state.deviceType = '';
    },
    SET_AUTH(state: UserState, payload: Auth) {
        state.auth = payload;
    },
    SET_USER(state: UserState, payload: User) {
        state.user = payload;
    },
    SET_VIEWING_USER(state: UserState, payload: User) {
        state.viewing = payload;
    },
    RESET_DASHBOARD_DATA(state: UserState) {
        state.dashboardData = undefined;
        state.currentLevel = undefined;
        state.nextTestOrQuestion = undefined;
        state.nextPlan = undefined;
        state.upgradeStatus = 'not_upgradeable';
        state.firstFillableStep = undefined;
    },
    SET_DASHBOARD_DATA(state: UserState, payload: DashboardData) {
        state.dashboardData = payload;
        state.currentLevel = payload.levels.find((level: DashboardLevel) => level.level === payload.current_level);
        state.upgradeStatus = 'not_upgradeable';

        const levelOne = payload.levels.find((level: DashboardLevel) => level.level === 1);
        if (levelOne) {
            const deviceTest = levelOne.data.find((levelData: any) => levelData.slug === 'jouw-toestel' && levelData.completed);
            if (deviceTest) {
                state.deviceType = deviceTest.score === 0 ? 'open' : 'closed';
            }
        }

        if (state.currentLevel) {
            state.upgradeStatus = state.currentLevel.upgrade_status;

            const notCompletedTestsOrQuestion = state.currentLevel.data.
                filter((level: { completed: boolean; type: string }) => !level.completed && (level.type === 'test' || level.type === 'question'));
            if (notCompletedTestsOrQuestion.length > 0) {
                state.nextTestOrQuestion = notCompletedTestsOrQuestion[0];
            }

            const notCompletedPlan = state.currentLevel.data.filter((level: { completed: boolean; type: string }) => !level.completed && level.type === 'plan');
            if (notCompletedPlan.length > 0) {
                state.nextPlan = notCompletedPlan[0];
            }

            if (state.nextPlan) {
                state.firstFillableStep = undefined;
                const interval = state.nextPlan.interval;

                state.nextPlan.periods.some((period: PlanPeriod) => {
                    // @ts-ignore
                    const startMoment = this._vm.$moment(period.start_date, 'Y-MM-DD');
                    // @ts-ignore
                    const startMomentWithInterval = this._vm.$moment(period.start_date, 'Y-MM-DD').add(interval - 1, 'd');
                    // @ts-ignore
                    let endMoment = this._vm.$moment();
                    if (!endMoment.isBefore(period.end_date, 'd')) {
                        // @ts-ignore
                        endMoment = this._vm.$moment(period.end_date, 'Y-MM-DD');
                    }

                    if (endMoment.isBefore(startMomentWithInterval) || endMoment.isSame(startMoment, 'd')) {
                        return true;
                    }
                    const diffInDays = endMoment.diff(startMoment, 'd') + 1;

                    if (period.usage.length < Math.round((diffInDays / interval))) {
                        state.firstFillableStep = period;
                        return true;
                    }
                });
            }
        }

    },
    SET_SCHEDULE(state: UserState, payload: any) {
        state.schedule = payload;
    },
    SET_DAILY_CIGARETTES(state: UserState, payload: number) {
        if (state.auth) {
            localStorage.setItem(`${process.env.VUE_APP_NAME}_DAILY_CIGARETTES_${state.auth.id}`, payload + '');
        }
        state.dailyCigarettes = payload;
    },
    SET_TOUR_VIEWING_STATUS(state: UserState, status: boolean) {
        localStorage.setItem(`${process.env.VUE_APP_NAME}_TOUR_VIEWING_STATUS`, status + '');
        state.tourViewed = status;
    },
};

const actions: ActionTree<UserState, RootState> = {
    login({ commit }, payload: LoginPayload): any {
        // @ts-ignore
        return this._vm.$api.post('login', payload, { withCredentials: true })
            .then((data: { data: any }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    logout({ commit, dispatch }): any {
        // @ts-ignore
        return this._vm.$api.post('logout', {}, { withCredentials: true })
            .then((data: { data: any }) => {
                dispatch('reset', null, { root: true });
                return Promise.resolve(data);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    register({ commit }, payload: RegisterPayload): any {
        // @ts-ignore
        return this._vm.$api.post('register', payload)
            .then((data: { data: any }) => {
                commit('SET_TOUR_VIEWING_STATUS', false);
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    emailConfirm({ commit }, payload: EmailConfirmPayload): any {
        // @ts-ignore
        return this._vm.$api.post('email/confirm', payload)
            .then((data: { data: any }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    emailConfirmReset({ commit }, payload: EmailConfirmResetPayload): any {
        // @ts-ignore
        return this._vm.$api.post('email/confirm/reset', payload)
            .then((data: { data: any }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    passwordForgot({ commit }, payload: PasswordForgotPayload): any {
        // @ts-ignore
        return this._vm.$api.post('password/forgot', payload)
            .then((data: { data: any }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    passwordReset({ commit }, payload: PasswordResetPayload): any {
        // @ts-ignore
        return this._vm.$api.post('password/reset', payload)
            .then((data: { data: any }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    changePassword({ commit }, payload: ChangePasswordPayload): any {
        // @ts-ignore
        return this._vm.$api.post('password/update', payload)
            .then((data: { data: any }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    me({ commit, state }, forceUpdate: boolean): any {

        if (state.auth && !forceUpdate) {
            return new Promise((resolve) => resolve(state.auth));
        }

        // @ts-ignore
        return this._vm.$api.get('me', { withCredentials: true })
            .then((response: { data: Auth }) => {
                commit('SET_AUTH', response.data);

                // Demo purposes
                if (response.data) {
                    commit('SET_DAILY_CIGARETTES', parseInt(localStorage.getItem(`${process.env.VUE_APP_NAME}_DAILY_CIGARETTES_${response.data.id}`) || '0', 10) || 15);
                }
                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    read({ commit, state }, id: number): any {

        if (state.user && state.user.id === id) {
            return new Promise((resolve) => resolve(state.user));
        }

        // @ts-ignore
        return this._vm.$api.get(`users/${id}`, { withCredentials: true })
            .then((response: { data: User }) => {
                commit('SET_USER', response.data);

                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => Promise.reject(e));
    },
    update({ commit, state }, payload: User): any {

        // @ts-ignore
        return this._vm.$api.put(`users/${payload.id}`, payload, { withCredentials: true })
            .then((response: { data: User }) => {
                commit('SET_USER', response.data);

                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => Promise.reject(e));
    },
    view({ commit, state }, id: number): any {

        if (state.viewing && state.viewing.id === id) {
            return new Promise((resolve) => resolve(state.viewing));
        }

        // @ts-ignore
        return this._vm.$api.get(`users/${id}`, { withCredentials: true })
            .then((response: { data: User }) => {
                commit('SET_VIEWING_USER', response.data);

                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => Promise.reject(e));
    },
    invite({ commit }, payload: UserInvitePayload): any {
        // @ts-ignore
        return this._vm.$api.post(`user-invites`, payload, { withCredentials: true })
            .then((data: { data: User }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    attach({ commit }, payload: { consultant_id: number, user_id: number }): any {
        // @ts-ignore
        return this._vm.$api.put(`consultants/${payload.consultant_id}/users/${payload.user_id}`, {}, { withCredentials: true })
            .then((data: { data: User }) => {
                return Promise.resolve();
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    readInviteCode({ commit }, payload: string): any {
        // @ts-ignore
        return this._vm.$api.get(`/invites/${payload}`)
            .then((response: { data: InviteCodeResponse }) => {
                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    resetStatistics({ commit }): any {
        commit('RESET_DASHBOARD_DATA');
    },
    readStatistics({ commit, state }, payload: { userId: number, update?: boolean }): any {

        if (state.dashboardData && !payload.update) {
            return new Promise((resolve) => resolve(state.dashboardData));
        }

        // @ts-ignore
        return this._vm.$api.get(`/users/${payload.userId}/dashboard-statistics`, { withCredentials: true })
            .then((response: { data: DashboardData }) => {
                commit('SET_DASHBOARD_DATA', response.data);

                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    levelUp({ commit }, payload: { userId: number, level: number }): any {
        // @ts-ignore
        return this._vm.$api.post(`/users/${payload.userId}/levels`, { level: payload.level }, { withCredentials: true })
            .then((response: { data: {} }) => {

                return Promise.resolve(true);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    mySchedule({ commit, state }, payload: number): any {

        if (state.schedule) {
            return new Promise((resolve) => resolve(state.schedule));
        }

        // @ts-ignore
        return this._vm.$api.get(`/users/${payload}/nap`, { withCredentials: true })
            .then((response: { data: any }) => {

                commit('SET_SCHEDULE', response.data);

                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    updateScheduleActual({ commit, state }, payload: { userId: number, periodId: number, start: string, end: string, amount: number }): any {

        // @ts-ignore
        return this._vm.$api.post(`/users/${payload.userId}/periods/${payload.periodId}/usage`, payload, { withCredentials: true })
            .then((response: { data: any }) => {

                return Promise.resolve(response.data);
            })
            .catch((e: ErrorResponse) => {
                return Promise.reject(e);
            });
    },
    setDailyCigarettes({ commit }, payload: number): any {
        commit('SET_DAILY_CIGARETTES', payload);
    },
    setTourStatus({ commit }, payload: boolean): any {
        commit('SET_TOUR_VIEWING_STATUS', payload);
    },
    unsubscribe({ commit, state }, payload: User): any {
        // @ts-ignore
        return this._vm.$api.post(`users/${payload.id}/unsubscribe`, payload, { withCredentials: true })
          .then((response: { data: User }) => {
              commit('SET_USER', response.data);

              return Promise.resolve(response.data);
          })
          .catch((e: ErrorResponse) => Promise.reject(e));
    },
};

export default {
    namespaced: true,
    state: rootState,
    getters,
    actions,
    mutations,
};
